import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemAgeCategoryType, AnalysisClientItemRelationType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPlannedChild } from "Infrastructure/Utils/ClientUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { FamilyMember } from "../Family/FamilyMember";
import { FamilyMemberDrawer } from "../Family/FamilyMemberDrawer";
import { AdditionalInformation } from "./AdditionalInformation";
import { ClientSolution } from "./ClientSolution";
import { RiskInsurance } from "./RiskInsurance";
import { StateAid } from "./StateAid";

type Props = {
  analysisId: string;
  versionId: string;
};

export const InsurancePane: FC<Props> = ({ analysisId, versionId }) => {
  const { readOnly, canAddFamilyMember } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId);

  const defaultFamilyMember = clientRelationsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [familyMemberId, setFamilyMemberId] = useState(defaultFamilyMember?.guid);
  useEffect(() => {
    setFamilyMemberId(defaultFamilyMember?.guid);
  }, [defaultFamilyMember?.guid]);

  const currentFamilyMember = clientRelationsData?.clients.find((client) => client.guid === familyMemberId);

  return (
    <Box sx={{ marginBottom: "120px !important" }}>
      <Stack direction="row">
        <Stack spacing={Spacing.XXXL} pr={Spacing.XXXL} flex={4}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography variant="h3">{t(Resources.AnalysisDetail.TabMenu.Insurance)}</Typography>
              <Typography variant="body2" color="secondary">
                {t(Resources.AnalysisDetail.Insurance.Subtitle)}
              </Typography>
            </Box>
            <Button
              disabled={!canAddFamilyMember}
              tooltip={!readOnly && !canAddFamilyMember ? t(Resources.AnalysisDetail.Family.AddMemberOwnerOnly) : undefined}
              color="secondary"
              startAdornment={<AddIcon />}
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              {t(Resources.AnalysisDetail.Insurance.AddMember)}
            </Button>
          </Box>
          {clientRelationsData && (
            <Grid container spacing={Spacing.L}>
              {clientRelationsData.clients
                .filter((client) => !isPlannedChild(client))
                .map((client) => {
                  return (
                    <Grid size={4} key={client.guid}>
                      <FamilyMember
                        birthDate={client.birthDate || ""}
                        clientId={client.guid}
                        firstName={client.firstName}
                        lastName={client.surname}
                        clientRelationType={client.relationType}
                        selected={familyMemberId === client.guid}
                        onSelect={(clientId) => setFamilyMemberId(clientId)}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          )}
          {currentFamilyMember?.ageCategoryType === AnalysisClientItemAgeCategoryType.adult && (
            <StateAid clientId={familyMemberId} analysisId={analysisId} versionId={versionId} />
          )}
          <Divider />
          {!!familyMemberId && (
            <RiskInsurance
              isAdult={currentFamilyMember?.ageCategoryType === AnalysisClientItemAgeCategoryType.adult}
              clientId={familyMemberId}
              analysisId={analysisId}
              versionId={versionId}
            />
          )}
        </Stack>
        {/* !important here because of parent spacing */}
        <Divider orientation="vertical" flexItem sx={{ margin: "-24px 0 0 0 !important" }} />
        <Stack spacing={Spacing.XL} mt={Spacing.S} px={Spacing.XXXL} sx={{ flex: 2 }}>
          {!!familyMemberId && <ClientSolution analysisId={analysisId} versionId={versionId} clientId={familyMemberId} />}
          <Divider />
          <AdditionalInformation analysisId={analysisId} versionId={versionId} clientId={familyMemberId} />
        </Stack>
      </Stack>

      <FamilyMemberDrawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Box>
  );
};
